//@ts-nocheck
import {
  Airplane,
  AirplaneTakeoff,
  Buildings,
  Boat,
  Bus,
  Car,
  Compass,
  Package,
  Parachute,
  Tram,
  Sun,
  Gear,
  File,
  TextT,
  RadioButton,
  CheckSquare,
  Article,
  Phone,
  Envelope,
  Calendar,
  NumberCircleOne
} from '@phosphor-icons/react';
import * as P from '@phosphor-icons/react';
import {
  getBucketImageUrl,
  getBucketFileUrl,
  getFileFromUrl
} from '../resourcePathHelper';
import { imageSize, pathReference } from '../../constants/files';
import {
  utcStringToLocalDate,
  utcTimeISO,
  convertInputDate
} from '../dateUtility';
import {
  serviceTypeFields,
  serviceTypes as serviceTypesConstants
} from '../../constants/serviceTypes';
import { getLineItemServiceType } from '../../constants/lineItemServiceTypes';
import { isEmptyValue } from '../common';
import { SERVICE_STOCK_PHOTO_TYPE } from '../../services/photo/serviceStockPhoto';

const iconValueByCode = {
  'day': Sun,
  'additional': File,
  'service': Gear
};

/**
 * This array contains the templates icons for service types.
 */

export const serviceTypes = [
  {
    id: 1,
    icon: <Parachute size={17} />,
    code: 'MISC',
    name: 'Misc'
  },
  {
    id: 2,
    icon: <Buildings size={17} />,
    code: 'HTL',
    name: 'Hotel'
  },
  {
    id: 3,
    icon: <Boat size={17} />,
    code: 'CRU',
    name: 'Cruise'
  },
  {
    id: 3,
    icon: <Compass size={17} />,
    code: 'SST',
    name: 'Tour'
  },
  {
    id: 5,
    icon: <Bus size={17} />,
    code: 'TRF',
    name: 'Transfer'
  },
  {
    id: 6,
    icon: <AirplaneTakeoff size={17} />,
    code: 'FLT',
    name: 'Flight'
  },
  {
    id: 7,
    icon: <Car size={17} />,
    code: 'CAR',
    name: 'Car Rental'
  },
  {
    id: 8,
    icon: <Tram size={17} />,
    code: 'TRN',
    name: 'Train'
  },
  {
    id: 9,
    icon: <Package size={17} />,
    code: 'PKG',
    name: 'Package'
  },
  {
    id: 10,
    icon: <Airplane size={17} />,
    code: 'AIRF',
    name: 'Airfare'
  }
];

export const formatServiceTypeOptions = (serviceTypes, onAction) => {
  return serviceTypes.map((sType) => {
    const { iconref } = sType || {};
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
    return {
      id: `service-types-option-${sType.code}`,
      name: sType.name,
      onClick: (evt) => onAction(evt, sType),
      icon: ServiceTypeIcon ? <ServiceTypeIcon size={17} /> : null,
      divider: !!sType.divider
    };
  });
};

export const generateServiceTypeFieldKey = (sTypeField) =>
  sTypeField?.title?.toLowerCase()?.split()?.join('-')?.replaceAll(' ', '');

export const generateServiceTypeFieldId = (sTypeField) =>
  `${sTypeField?.servicefieldtype?.key}-${generateServiceTypeFieldKey(sTypeField)}`;

export const generateGridRows = (
  rowFields = [],
  serviceType = {},
  useHardCodedSettings = false,
  isLibrary = false
) => {
  const rows = {};

  if (serviceType && useHardCodedSettings) {
    // Get hardcoded field settings if needed
    const hardcodedFieldsMap = useHardCodedSettings
      ? serviceTypeFields
          .find((sType) => sType.code === serviceType?.code)
          ?.servicetypefield?.reduce((map, field) => {
            if (field.key) map[field.key] = field;
            return map;
          }, {}) || {}
      : {};

    rowFields?.forEach((field) => {
      let processedField = field;

      // Apply hardcoded settings if available
      if (useHardCodedSettings && hardcodedFieldsMap[field.key]) {
        const hardcodedField = hardcodedFieldsMap[field.key];
        processedField = {
          ...field,
          row: hardcodedField?.row ?? field?.row,
          col: hardcodedField?.col ?? field?.col,
          colsize: hardcodedField?.colsize ?? field?.colsize,
          isenabled: isLibrary
            ? true
            : (hardcodedField?.isenabled ?? field?.isenabled),
          isrequired: hardcodedField?.isrequired ?? field?.isrequired,
          description: hardcodedField?.description ?? field?.description
        };
      }

      const rowNumber = processedField?.row || 1;

      if (!rows[rowNumber]) {
        rows[rowNumber] = [];
      }

      rows[rowNumber].push(processedField);
    });
  } else {
    rowFields?.forEach((field) => {
      if (!rows[field?.row]) {
        rows[field?.row] = [];
      }
      rows[field?.row].push(field);
    });
  }

  return rows;
};

export const generateGridColumns = (rowFields = []) => {
  const sortedFields = [...rowFields]?.sort((a, b) => a?.col - b?.col);
  const columnsList = [];
  let currentColumn = 1;

  for (const field of sortedFields) {
    const diff = field?.col - currentColumn;
    if (diff > 0) {
      columnsList.push({
        colsize: diff,
        col: currentColumn,
        isEmptyColumn: true
      });
    }
    columnsList.push(field);
    currentColumn = field?.col + field?.colsize;
  }

  // Handle trailing empty columns (only if needed)
  if (currentColumn <= 12) {
    columnsList.push({
      colsize: 12 - currentColumn + 1,
      col: currentColumn,
      isEmptyColumn: true
    });
  }

  return columnsList;
};

export const generateCheckBoxOptions = (options = []) => {
  return options?.map((option, index) => {
    return {
      tripdetaileddayservicevalueid: option?.tripdetaileddayservicevalueid,
      optionId: option?.optionId || option?.servicefieldoptionid,
      servicefieldoptionid: option?.servicefieldoptionid,
      value: option?.value || option?.title,
      sequence: option?.sequence ?? index + 1
    };
  });
};

export const getValueFromServiceField = (serviceField) => {
  return serviceField?.options ? serviceField?.options[0]?.value : null;
};

export const setOptionValuesFromServiceField = (
  serviceField,
  defaultOptions,
  optionValues
) => {
  // For checkboxes (multiple options)
  serviceField.options = serviceField.options.map((option) => {
    // Find the matching fieldOption
    const fieldOption =
      defaultOptions.find(
        (opt) => opt.servicefieldoptionid === option.servicefieldoptionid
      ) || {}; // Provide a fallback to avoid undefined errors

    // Find the matching optionValue
    const matchedOption =
      optionValues.find(
        (opt) => opt.servicefieldoptionid === option.servicefieldoptionid
      ) || {};

    return {
      ...fieldOption,
      tripdetaileddayservicevalueid:
        matchedOption.tripdetaileddayservicevalueid || null,
      value: matchedOption.value ?? null // Ensure safe access
    };
  });

  return serviceField;
};

export const setOptionValueFromServiceField = (
  serviceField,
  optionValues,
  multiple = false
) => {
  const options = optionValues.map((option) => ({
    ...option,
    tripdetaileddayservicevalueid: !multiple
      ? (serviceField.options[0].tripdetaileddayservicevalueid ?? null)
      : (option.tripdetaileddayservicevalueid ?? null),
    servicefieldoptionid: serviceField.servicefieldoptionid ?? null,
    servicefieldid: serviceField.servicefieldid ?? null,
    isvisible: serviceField.isvisible,
    value: typeof option === 'string' || !option ? option : option.value
  }));
  serviceField.options = options;

  return serviceField;
};

export const validateFormData = (fieldSettings, formData, t) => {
  let isValid = true;
  const formDataCopy = { ...formData };

  for (const fieldSetting of fieldSettings) {
    if (fieldSetting?.isrequired) {
      const serviceFieldId = fieldSetting?.servicefieldid;
      const formDataValue = formDataCopy[serviceFieldId];

      if (
        !formDataValue ||
        !formDataValue?.options ||
        !formDataValue?.options?.length
      ) {
        isValid = false;
      } else if (
        formDataValue?.options?.length === 1 &&
        !formDataValue?.options[0]?.value?.trim()
      ) {
        isValid = false;
        formDataCopy[serviceFieldId].error =
          `${fieldSetting.title} ${t('common:isRequired')}`;
      }
    }
  }

  return { isValid, newFormData: formDataCopy };
};

/**
 * Initializes the service type form data by setting default values to null.
 *
 * @param {Object} serviceType - The service type object containing service fields.
 * @returns {Object} - An object representing the initial form data where each field ID is mapped to null.
 */
export const initServiceTypeDayFormData = (
  serviceType,
  itineraryNavigation
) => {
  const isAccommodation =
    serviceType?.code === serviceTypesConstants.ACCOMMODATION;

  return serviceType?.servicetypefield?.reduce((formData, sTypeField) => {
    const hasMultipleOptions = ['checkbox', 'select'].includes(
      sTypeField?.servicefieldtype?.key
    );
    const hasFiles = ['image', 'file'].includes(
      sTypeField?.servicefieldtype?.key
    );

    const isNumberOfNightsField = sTypeField?.key === 'NUMBEROFNIGHTS';

    const options = hasMultipleOptions
      ? [
          {
            tripdetaileddayservicevalueid: null,
            servicefieldoptionid: sTypeField?.servicefieldoptionid ?? null,
            value: null
          }
        ]
      : [
          {
            servicefieldoptionid: null,
            value:
              isAccommodation && isNumberOfNightsField && itineraryNavigation
                ? 1
                : null,
            tripdetaileddayservicevalueid: null
          }
        ];

    formData[sTypeField.servicefieldid] = {
      servicefieldid: sTypeField?.servicefieldid ?? null,
      error: null,
      options,
      isvisible: true,
      servicefieldoptionid: sTypeField?.servicefieldoptionid ?? null,
      key: sTypeField.key ?? null,
      ...(hasFiles && { blobFiles: [] })
    };
    return formData;
  }, {});
};

export const getDefaultFieldOptions = (serviceType, servicefieldid) => {
  const field = serviceType.servicetypefield.find(
    (field) => field.servicefieldid === servicefieldid
  );

  if (!field) return [];

  return field.servicefieldoption.reduce((accumulator, option) => {
    accumulator[option.servicefieldoptionid] = option;
    return accumulator;
  }, {});
};

const getResourceUrl = (
  option,
  typeKey = 'file',
  data = {},
  pathReference,
  sizePrefix = ''
) => {
  const config = {
    fileName: option?.value ?? '',
    pathReference,
    ...data
  };

  return typeKey === 'file'
    ? getBucketFileUrl({
        ...config
      })
    : getBucketImageUrl({
        ...config,
        sizePrefix: sizePrefix || imageSize.thumbnail.prefix
      });
};

export const setServiceTypeDayFormData = async (
  serviceType,
  fieldTypesMap,
  data = {},
  isLibrary = false,
  itineraryNavigation = false
) => {
  const formData = {}; // Initialize formData outside the reduce
  const imagePathReference = isLibrary
    ? pathReference.LIBRARY
    : pathReference.DETAILED_DAY_SERVICE;

  for (const sTypeFieldItem of serviceType.servicefields) {
    const sTypeField = structuredClone(sTypeFieldItem);
    const isNumberOfNightsField = sTypeField?.key === 'NUMBEROFNIGHTS';
    const fieldValue = sTypeField?.options[0]?.value;
    const fieldType = fieldTypesMap[sTypeField.servicefieldid];
    const defaultField = serviceType.servicetypefield.find(
      (field) => field.servicefieldid === sTypeField.servicefieldid
    );

    if (!sTypeField.options || sTypeField.options.length === 0) {
      formData[sTypeField.servicefieldid] = sTypeField;
      continue;
    }

    // Set number of nights to 1 when no value is provided
    if (
      isNumberOfNightsField &&
      isEmptyValue(fieldValue?.trim()) &&
      //!isLibrary &&
      itineraryNavigation
    ) {
      sTypeField.options[0].value = 1;
    }

    switch (fieldType?.key) {
      case 'time':
        // For time fields, keep as ISO string to avoid timezone conversion issues
        // The time picker will handle conversion to/from Date objects
        formData[sTypeField.servicefieldid] = setOptionValueFromServiceField(
          sTypeField,
          sTypeField.options.map((opt) => {
            // Keep time as ISO string, don't convert to Date
            // This preserves the "wall clock" time without timezone shifts
            return {
              ...opt,
              value: opt.value || null
            };
          })
        );
        break;
      case 'date':
        formData[sTypeField.servicefieldid] = setOptionValueFromServiceField(
          sTypeField,
          sTypeField.options.map((opt) => {
            let localDate = null;
            if (opt.value) {
              // Convert string to a Date with UTC time but local time
              localDate = utcStringToLocalDate(opt.value);
            }
            return {
              ...opt,
              value: opt.value ? localDate : null
            };
          })
        );
        break;
      case 'checkbox':
        formData[sTypeField.servicefieldid] = setOptionValuesFromServiceField(
          sTypeField,
          defaultField.servicefieldoption,
          sTypeField.options
        );
        break;
      case 'image':
      case 'file':
        {
          const fieldServiceValueIdName = isLibrary
            ? 'itineraryservicevalueid'
            : 'tripdetaileddayservicevalueid';
          const optionsWithFiles = await Promise.all(
            sTypeField?.options
              ?.filter((option) => !!option?.value)
              ?.map(async (option) => {
                const url = getResourceUrl(
                  option,
                  fieldType?.key,
                  data,
                  imagePathReference,
                  isLibrary ? imageSize.large.prefix : null
                );
                const file = await getFileFromUrl(
                  url,
                  option.value,
                  option?.value?.split('.')?.pop()
                );
                const metadata = sTypeField.files?.find(
                  (item) =>
                    item[fieldServiceValueIdName] ===
                    option[fieldServiceValueIdName]
                );
                const isStockPhoto =
                  fieldType.key === 'image' &&
                  metadata?.type === SERVICE_STOCK_PHOTO_TYPE;
                const newOption = {
                  ...option,
                  presignType: fieldType.key,
                  url: url,
                  file: file,
                  isNew: false,
                  ...(isStockPhoto && {
                    stockImported: true,
                    stockTitle: metadata.originalname ?? ''
                  })
                };

                if (isLibrary) {
                  newOption['path'] = pathReference.LIBRARY;
                }
                return newOption;
              })
          );

          const files = sTypeField?.files?.map((file) => ({
            ...file,
            name:
              sTypeField?.options?.find(
                (option) =>
                  option[fieldServiceValueIdName] ===
                  file[fieldServiceValueIdName]
              )?.value ?? file?.name
          }));

          const newSTypeField = {
            ...sTypeField,
            files: files
          };
          formData[sTypeField.servicefieldid] = setOptionValueFromServiceField(
            newSTypeField,
            optionsWithFiles,
            true
          );
        }

        break;
      default:
        formData[sTypeField.servicefieldid] = setOptionValueFromServiceField(
          sTypeField,
          sTypeField.options.map((opt) => ({
            ...opt,
            value: opt.value?.toString() || ''
          }))
        );
        break;
    }
  }

  return formData;
};

export const getFormPayload = (serviceType, formData, isLibrary) => {
  const { servicetypefield = [] } = serviceType || {};
  const fieldServiceValueIdName = isLibrary
    ? 'itineraryservicevalueid'
    : 'tripdetaileddayservicevalueid';

  return {
    servicetypeid: serviceType.servicetypeid,
    servicefields: Object.keys(formData).reduce(
      (accumulator, fieldId, index) => {
        const { options: fieldOptions, files = [] } = formData[fieldId];
        const isFile = fieldOptions.some((option) => option?.file != null);

        // Find the field to check if it's a time or date field
        const field = servicetypefield.find(
          (field) => field.servicefieldid === parseInt(fieldId, 10)
        );
        const isTimeField = field?.servicefieldtype?.key === 'time';
        const isDateField = field?.servicefieldtype?.key === 'date';

        accumulator.push({
          servicefieldid: parseInt(fieldId, 10),
          isvisible: true,
          isFile,
          fieldIndex: index,
          files,
          options: fieldOptions.map((option) => {
            const tripdetaileddayservicevalueid =
              option?.tripdetaileddayservicevalueid ?? null;
            const itineraryservicevalueid =
              option?.itineraryservicevalueid ?? null;
            const fieldServiceValueIdValue = isLibrary
              ? itineraryservicevalueid
              : tripdetaileddayservicevalueid;

            // For time and date fields, ensure proper format conversion
            let value = option.value;
            if (value instanceof Date) {
              if (isTimeField) {
                value = utcTimeISO(value);
              } else if (isDateField) {
                value = convertInputDate(value);
              }
            }
            // If value is already an ISO string (e.g., from library or unchanged),
            // keep it as-is to preserve the wall clock time

            return {
              ...option,
              value,
              [fieldServiceValueIdName]: fieldServiceValueIdValue,
              isNew: !fieldServiceValueIdValue ?? false
            };
          })
        });
        return accumulator;
      },
      []
    )
  };
};

export const iconExists = (iconName) => {
  return typeof P[iconName] === 'object';
};

const getPassengerFieldIcon = (field, size, color) => {
  switch (field?.fieldtype?.key) {
    case 'textbox':
      return <TextT size={size} color={color} />;
    case 'radiobutton':
    case 'boolean':
      return <RadioButton size={size} color={color} />;
    case 'checkbox':
      return <CheckSquare size={size} color={color} />;
    case 'textarea':
      return <Article size={size} color={color} />;
    case 'phone':
      return <Phone size={size} color={color} />;
    case 'email':
      return <Envelope size={size} color={color} />;
    case 'date':
      return <Calendar size={size} color={color} />;
    case 'number':
      return <NumberCircleOne size={size} color={color} />;
    default:
      return <TextT size={size} color={color} />;
  }
};

export const getLibraryItemIcon = (
  item,
  { size = 20, color = '#667085' } = {}
) => {
  // Check if this is a LineItem (new refactored services)
  if (item?.kind === 'lineitem' || item?.lineitemid) {
    const mapped = getLineItemServiceType(item?.servicetypecode);
    const iconref = mapped?.iconref;
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
    if (!ServiceTypeIcon) return <Gear size={size} color={color} />;
    return <ServiceTypeIcon size={size} color={color} />;
  }

  // Get code from contenttype relation (for regular content) or use 'service' for service items
  const code =
    item?.contenttype?.code || (item?.isservice ? 'service' : item?.code);

  if (code === 'day') {
    return <Sun size={size} color={color} />;
  }

  if (code === 'passengerfield') {
    return getPassengerFieldIcon(item, size, color);
  }

  if (code === 'service') {
    const iconref =
      item?.service?.servicetype?.iconref ||
      item?.servicetype?.iconref ||
      item?.servicetypeiconref ||
      item?.servicetype?.servicetypeiconref;
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
    return ServiceTypeIcon ? (
      <ServiceTypeIcon size={size} color={color} />
    ) : (
      <Gear size={size} color={color} />
    );
  }

  return <File size={size} color={color} />;
};

export const getLibraryItemIconRef = (item) => {
  if (item?.code !== 'service') return null;
  return (
    item?.service?.servicetype?.iconref ||
    item?.servicetype?.iconref ||
    item?.servicetypeiconref ||
    item?.servicetype?.servicetypeiconref ||
    null
  );
};

export const getServiceTypeIcon = (serviceType) => {
  if (serviceType?.kind === 'lineitem' || serviceType?.lineitemid) {
    const mapped = getLineItemServiceType(serviceType?.servicetypecode);
    const iconref = mapped?.iconref;
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
    if (!ServiceTypeIcon) return <Gear size={20} color='#667085' />;
    return <ServiceTypeIcon size={20} color='#667085' />;
  }

  if (serviceType.isservice) {
    const iconref =
      serviceType?.servicetypeiconref ||
      serviceType?.servicetype?.servicetypeiconref;
    const ServiceTypeIcon = iconExists(iconref) ? P[iconref] : null;
    if (!ServiceTypeIcon) return <Gear size={20} color='#667085' />;

    return <ServiceTypeIcon size={20} color='#667085' />;
  }

  // Default icon fallback for regular content - use code instead of ID
  const DefaultIcon = iconValueByCode[serviceType.code] || File;
  return <DefaultIcon size={20} color='#667085' />;
};

export const getSingleFieldValueByKey = (formData = {}, key = '') => {
  return Object.values(formData).find((field) => field.key === key)
    ?.options?.[0]?.value;
};

export const setSingleFieldValueByKey = (formData = {}, key = '', newValue) => {
  const field = Object.values(formData).find((field) => field?.key === key);
  if (field) {
    field.options[0].value = newValue;
  }
  return field;
};

export const focusFieldInput = (formRefs, fieldId = 'textbox-title') => {
  const titleInput =
    formRefs?.current[fieldId]?.getElementsByTagName('input')[0];

  if (titleInput) {
    titleInput.focus();
  }
};
